import React from 'react';

/**
 * Visual variant for the primary text in the cell.
 *
 * - `default`  — regular weight, gray (`text-sm text-gray-600`)
 * - `primary`  — medium weight, dark/black (`text-sm font-medium text-foreground`)
 * - `amount`   — medium weight, dark/black (`text-sm font-medium text-foreground`)
 * - `muted`    — regular weight, light muted (`text-sm text-muted-foreground`)
 */
export type CellTextVariant = 'default' | 'primary' | 'amount' | 'muted';

export interface CellTextProps {
  /** Primary value to display */
  value?: React.ReactNode;
  /** Visual variant controlling text weight & color */
  variant?: CellTextVariant;
  /** Optional second line — rendered smaller and muted below the value */
  desc?: React.ReactNode;
  /** Fallback shown (in muted style) when value is falsy. Defaults to "N/A" */
  fallback?: string;
  /** Optional className override for the primary text span */
  className?: string;
}

const variantClasses: Record<CellTextVariant, string> = {
  default: 'text-sm text-gray-600',
  primary: 'text-sm font-medium text-foreground',
  amount: 'text-sm font-medium text-foreground',
  muted: 'text-sm text-muted-foreground',
};

/**
 * CellText — Standardized text renderer for table cells.
 *
 * Ensures consistent typography across all tables (order, invoice, etc.).
 * Use for any text-based cell content. For component cells (status badges,
 * action buttons, route displays) render them directly instead.
 *
 * @example
 * ```tsx
 * // Simple text
 * <CellText value="John Doe" />
 *
 * // Primary (bold) with a description line
 * <CellText variant="primary" value="Feb 12, Wed" desc="2 days left" />
 *
 * // Amount
 * <CellText variant="amount" value="1,437.5 SAR" />
 *
 * // Auto fallback when value is undefined
 * <CellText value={invoice.buyer?.name} />
 * ```
 */
export const CellText: React.FC<CellTextProps> = ({
  value,
  variant = 'default',
  desc,
  fallback = 'N/A',
  className,
}) => {
  // Falsy value → render fallback in muted style
  if (value === null || value === undefined || value === '') {
    return <span className={className ?? variantClasses.muted}>{fallback}</span>;
  }

  // No description → single-line
  if (!desc) {
    return <span className={className ?? variantClasses[variant]}>{value}</span>;
  }

  // With description → two-line layout
  return (
    <div className="flex flex-col gap-0.5">
      <span className={className ?? variantClasses[variant]}>{value}</span>
      <span className="text-xs text-muted-foreground">{desc}</span>
    </div>
  );
};
